import type { AssignmentStatus, CompletionSource, RtiCredit, TrainingAssignment } from "@lms/shared-types";
import type { Tone } from "../components/ui";

/**
 * The words the training screens use, in one place so Home, the list and
 * the item page never drift apart — and so a state is a word, never a
 * colour alone (the portal rule the tests enforce).
 */
export const STATUS_WORD: Record<AssignmentStatus, { word: string; tone: Tone }> = {
  assigned: { word: "To do", tone: "accent" },
  claimed: { word: "Waiting to be checked", tone: "warn" },
  completed: { word: "Done", tone: "ok" },
  withdrawn: { word: "Withdrawn", tone: "neutral" },
};

/** What a learner says they did, in their words rather than the column's. */
export const SOURCE_WORD: Record<CompletionSource, string> = {
  self_report: "You said you finished it",
  certificate: "You gave a certificate",
  attestation: "Your instructor confirmed it",
  vendor_xapi: "The provider told us",
};

/** "16 hours", "3 hours", "45 minutes" — never "960 minutes", which nobody plans a week around. */
export function durationWords(minutes?: number): string | undefined {
  if (!minutes) return undefined;
  if (minutes < 90) return `${minutes} minutes`;
  const hours = Math.round(minutes / 60);
  return `${hours} hour${hours === 1 ? "" : "s"}`;
}

/**
 * The one ordering every screen uses: what is late first, then what is due
 * soonest, then whatever has no date, with finished work last. The server
 * already sorts this way; a screen that re-sorts its own copy after an
 * action must sort it the same.
 */
export function inOrder(assignments: TrainingAssignment[]): TrainingAssignment[] {
  const rank = (a: TrainingAssignment) => (a.status === "completed" ? 2 : a.overdue ? 0 : 1);
  return [...assignments].sort((a, b) => rank(a) - rank(b) || (a.dueAt ?? "9999").localeCompare(b.dueAt ?? "9999") || a.item.title.localeCompare(b.item.title));
}

/** "16 hours", "1 hour", "2.5 hours". */
export function hoursWord(hours: number): string {
  const n = Number.isInteger(hours) ? String(hours) : String(Number(hours.toFixed(2)));
  return `${n} hour${hours === 1 ? "" : "s"}`;
}

/**
 * The ledger's day (YYYY-MM-DD, already the tenant's calendar) as an
 * instant `useFormat().date` can render without moving it: noon UTC is the
 * same calendar day in every time zone the product serves, where midnight
 * UTC is yesterday in Idaho.
 */
export const ledgerDay = (day: string): string => `${day}T12:00:00Z`;

/**
 * What an assignment does to the hours ledger, in the learner's words
 * (CNT-26). The server decides the state; this only says it — and says
 * *why* when nothing is banked, because "0" and "nothing, and here is why"
 * are different answers to somebody counting towards 144 hours a year.
 */
export function rtiWords(rti: RtiCredit, date: (iso: string) => string): string {
  switch (rti.state) {
    case "credited":
      return `${hoursWord(rti.creditedHours ?? 0)} of RTI banked${rti.creditedOn ? `, for ${date(ledgerDay(rti.creditedOn))}` : ""}`;
    case "pending":
      return `${hoursWord(rti.planHours ?? 0)} of RTI, banked once it is checked`;
    case "zero_hours":
      return "None — it is on your RTI plan at 0 hours";
    case "not_credited":
      return "None banked — it was checked before hours were recorded here";
    case "reversed":
      return "None — the hours it banked were corrected to nothing";
    case "not_on_plan":
      return "None — it is not on your program's RTI plan, so it banks no apprenticeship hours";
  }
}

/** What still needs the person to do something: not claimed, not done, not withdrawn. */
export const needsDoing = (a: TrainingAssignment): boolean => a.status === "assigned";

/**
 * The same states from the other side of the desk (Program › Assignments):
 * the person assigning reads "Assigned", not the learner's "To do".
 */
export const ROSTER_WORD: Record<AssignmentStatus, { word: string; tone: Tone }> = {
  assigned: { word: "Assigned", tone: "accent" },
  claimed: { word: "Waiting to be checked", tone: "warn" },
  completed: { word: "Done", tone: "ok" },
  withdrawn: { word: "Withdrawn", tone: "neutral" },
};

/** Still open, so it can be withdrawn (CNT-12): not finished and not already taken back. */
export const withdrawable = (a: TrainingAssignment): boolean => a.status === "assigned" || a.status === "claimed";

/**
 * A date field's `YYYY-MM-DD` as the end of that day where the person
 * assigning is, so "wanted by the 30th" is not late on the morning of the
 * 30th. Empty stays empty — a due date is optional (CNT-10).
 */
export function dueFromDateField(value: string): string | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  const at = new Date(`${value}T23:59:00`);
  return Number.isNaN(at.getTime()) ? undefined : at.toISOString();
}

/**
 * What a course counts for, in the roster's few words (Program › Assignments): the same ledger states as
 * `rtiWords`, said about somebody else rather than to them.
 */
export function rosterRtiWords(rti: RtiCredit): string {
  switch (rti.state) {
    case "credited":
      return `${hoursWord(rti.creditedHours ?? 0)} of RTI banked`;
    case "pending":
      return `${hoursWord(rti.planHours ?? 0)} of RTI when checked`;
    case "zero_hours":
      return "On the RTI plan at 0 hours";
    case "not_credited":
      return "Checked before hours were recorded — none banked";
    case "reversed":
      return "Banked, then corrected to nothing";
    case "not_on_plan":
      return "Not on the RTI plan — no hours";
  }
}
